import API from '@API/index';
import SiteMarker from '@classes/MapBox/SiteMarker';
import StreamMarker from '@classes/MapBox/StreamMarker';
import MapBoxBoundaries from '@classes/MapBoxBoundaries';
import { IStoreState } from '@store/index';
import Utils from '@utils/index';
import { first } from 'lodash';
import mapboxgl from 'mapbox-gl';
import React from 'react';
import { useSelector } from 'react-redux';

const EventContentMap: React.FC<{}> = () => {
  const streams = useSelector((store: IStoreState) => store.streams.streams);
  const sites = useSelector((store: IStoreState) => store.sites.sites);
  const selectedEvents = useSelector((store: IStoreState) => store.overviewView.selectedEvents);

  const [mapLoaded, setMapLoaded] = React.useState<boolean>(false);

  const mapContainer = React.useRef<HTMLDivElement | null>(null);
  const map = React.useRef<mapboxgl.Map | null>(null);
  const markers = React.useRef<mapboxgl.Marker[]>([]);
  const observer = React.useRef<ResizeObserver | null>(null);

  React.useEffect(() => {
    const element = document.getElementById("gei-map-container");

    if (element) {
      observer.current = new ResizeObserver(() => {
        if (map.current) map.current.resize();
      })
      observer.current?.observe(element)
    }

    return () => {
      const element = document.getElementById("gei-map-container");
      const obs = observer.current
      if (obs && element) obs.unobserve(element);
    }
  }, [])

  const load = React.useCallback(() => {
    if (mapContainer.current && !map.current) {
      let boundaries = [-131.74804688, 52.48278022, -68.46679688, 22.59372606] as any;

      boundaries = [-131.74804688, 52.48278022, -68.46679688, 22.59372606] as any;

      const mapOptions = {
        container: mapContainer.current,
        style: Utils.MapBox.generateStylesUrl('streets'),
        bounds: boundaries
      }

      map.current = API.MapBox.initializeMap(mapOptions);
      map.current.on('load', () => {
        map.current?.resize();
        setMapLoaded(true);
      });
    }
  }, [])

  React.useEffect(load, [load]);

  React.useEffect(() => {
    try {
      if (mapLoaded === false) return;
      markers.current.forEach(e => e.remove());

      if (selectedEvents.length > 1) return;

      const event = first(selectedEvents);

      if (event) {
        if (map.current) {
          const site = sites.get(event.companyId)?.find(site => site.siteId === event.siteId);
          if (site) {
            const marker = new SiteMarker()
              .setLngLat(new mapboxgl.LngLat(site.coordinates?.longitude || 0, site.coordinates?.latitude || 0))
              .setDraggable(false)
              .withLabel(map.current as mapboxgl.Map, site.name);

            marker.addTo(map.current);

            markers.current.push(marker);

            const kvsNames: Set<string> = new Set();

            kvsNames.add(event.sourceCamera);
            event.children?.forEach(child => kvsNames.add(child.sourceCamera));
            const s = streams.filter(el => Array.from(kvsNames).includes(el.kvsName) && el.latitude && el.longitude);
            s.forEach((e) => {
              const marker = new StreamMarker()
                .setLngLat(new mapboxgl.LngLat(e.longitude || 0, e.latitude || 0))
                .setDraggable(false)
                .withLabel(map.current as mapboxgl.Map, e.defaultName || e.kvsName);

              if (map.current) marker.addTo(map.current);
              markers.current.push(marker);
            })

            const siteStreams = [{ longitude: site.coordinates?.longitude, latitude: site.coordinates?.latitude } as VideoStream];
            map.current?.fitBounds(new MapBoxBoundaries(siteStreams).generate(0.002) as any, { animate: false });
          }
        }
      }
    } catch (e) {
      console.log(e)
    }
  }, [mapLoaded, selectedEvents, sites, streams])

  return (
    <div style={{ height: '10rem' }} ref={mapContainer} className="map-container" id="gei-map-container">

    </div>
  )
}

export default EventContentMap;
